import {
  flattenDeep,
  forEach,
  get,
  has,
  isEmpty,
  keys,
  map,
  reduce,
  set,
  trim,
  uniq,
} from "lodash"
import type { ComparableProduct } from "~/fragments/comparableProduct"
import type {
  LabelItem,
  TableGroup,
  TableItems,
  TableValue,
  Tooltip,
} from "../types"

type List = Record<string, Record<string, string>>

/**
 * Parses a plain-text parameters string (from the `flatData.parameters` field in Squidex)
 * into a nested map: { [groupName]: { [rowLabel]: value } }.
 *
 * Input format (one entry per line):
 *   - Line WITHOUT a colon → group header (e.g. "Storage")
 *   - Line WITH a colon    → row in "Label: value" format
 *   - Rows before the first header go into the "_" group
 *   - Empty lines are skipped
 *
 * Example input:
 *   Basic
 *   Storage: 100 GB
 *   RAM: 8 GB
 *
 *   Advanced
 *   CPU: 4 cores
 *
 * Example output:
 *   { "Basic": { "Storage": "100 GB", "RAM": "8 GB" }, "Advanced": { "CPU": "4 cores" } }
 *
 * @param parameters - Raw plain-text parameters string from Squidex `flatData.parameters`
 *
 * @returns Nested map of { [groupName]: { [rowLabel]: value } }
 */
const createParametersList = (parameters: string): List => {
  const output: List = {}

  // URLs contain "://" — temporarily replace with "#//" so split(":") doesn't break URL values
  const parametersWithoutTooltipLink: string = parameters.replaceAll(
    "://",
    "#//"
  )

  const lines: string[] = trim(parametersWithoutTooltipLink).split("\n")

  // active group; defaults to "_" for rows before any header
  let group: string = "_"

  forEach(lines, (line: string) => {
    if (isEmpty(line)) {
      return
    }

    if (line.indexOf(":") === -1) {
      // No colon → this is a group header
      group = line
    } else {
      // "Label: value" → store under the current group
      const colonIndex = line.indexOf(":")
      const name = line.slice(0, colonIndex)
      const value = line.slice(colonIndex + 1)
      set(output, [trim(group), trim(name)], trim(value))
    }
  })

  return output
}

/**
 * Parses the tooltip syntax embedded in a row label into a Tooltip structure.
 *
 * A tooltip can be plain text or contain an optional clickable link:
 *   - `[tooltip text]`                      → plain text tooltip
 *   - `[tooltip text <link label$href$>]`   → tooltip with an embedded link
 *
 * The `<label$href$>` syntax defines a link: label is the visible text, href is the URL.
 *
 * Examples:
 *   "[simple tooltip]"                    → "simple tooltip"
 *   "[click <here$https://home.pl$>]"     → ["click ", { title: "here", text: "here", href: "https://home.pl" }, ""]
 *
 * @param tooltip - Result of matching `[...]` brackets in a row label; only the first match is used
 *
 * @returns Plain string for text-only tooltips, or an array mixing strings and link objects when a link is present
 */
export const createTooltipContent = (tooltip: RegExpMatchArray): Tooltip => {
  // Strip square brackets and restore "://" that was escaped during parsing
  let tooltipContent: string = tooltip[0]
    .replace(/[[\]']+/g, "")
    .replaceAll("#//", "://")

  // Find all <...> segments — each one is a potential link
  const tooltipLinks: RegExpMatchArray | null =
    tooltipContent.match(/\<.*?\>+/g)

  if (tooltipLinks) {
    const tooltipContentWithLinks: Tooltip = []

    tooltipLinks.forEach((link: string, index: number) => {
      const linkStartIndex: number = tooltipContent.indexOf(link)
      const linkEndIndex: number = tooltipContent.indexOf(link) + link.length

      // Extract href — it is wrapped in "$...$" delimiters
      const linkHrefReg: RegExpMatchArray | null = link.match(/\$.*?\$+/g)
      const linkHref: string = linkHrefReg
        ? linkHrefReg[0].replace(/[$]+/g, "")
        : ""

      // Extract link text — the <> content minus the href and angle brackets
      const linkText: string = linkHrefReg
        ? link.replace(linkHrefReg[0], "").replace(/[<>]+/g, "")
        : ""

      const linkJsx = {
        title: linkText,
        text: linkText,
        href: linkHref,
      }

      // Append text before the link, then the link object itself
      tooltipContentWithLinks.push(
        tooltipContent.slice(0, linkStartIndex),
        linkJsx
      )

      // Advance cursor past the processed link
      tooltipContent = tooltipContent.slice(linkEndIndex, tooltipContent.length)

      // After the last link, append any remaining plain text (may be an empty string)
      if (tooltipLinks.length === index + 1) {
        tooltipContentWithLinks.push(tooltipContent)
      }
    })

    return tooltipContentWithLinks
  }

  // No links — return the tooltip as a plain string
  return tooltipContent
}

/**
 * Merges parameter data from multiple products into a unified list of table groups (TableGroup[]).
 *
 * Steps:
 *  1. Parse each product's parameters string into a List map (via createParametersList).
 *  2. Collect all unique group names across all products, preserving encounter order.
 *  3. For each group, collect all unique row labels across all products.
 *  4. For each label, build a values array — one cell per product (null when a product
 *     lacks that label), ensuring every row has the same number of columns.
 *  5. Detect optional "(subtitle)" and "[tooltip]" syntax in a label — when present,
 *     the label becomes a structured object instead of a plain string.
 *
 * @param products - List of products whose parameters should be merged
 *
 * @returns Array of table groups, each containing row items with one value cell per product
 */
function compareProductParameters(products: ComparableProduct[]): TableGroup[] {
  // Step 1: parse each product's parameters string into a List map
  const list: List[] = []

  forEach(products, (item: ComparableProduct): void => {
    list.push(createParametersList(item.flatData.parameters ?? ""))
  })

  // Step 2: unique group names in encounter order across all products
  const groupsNames = uniq(flattenDeep(map(list, keys)))

  const items: TableGroup[] = []

  forEach(groupsNames, (group: string) => {
    const groupItems: TableItems[] = []

    // Step 3: unique row labels for this group across all products
    const labels = uniq(
      reduce(
        map(list, (item) => keys(item[group])),
        (result: string[], value: string[]) => {
          forEach(value, (itemValue: string) => {
            result.push(itemValue)
          })

          return result
        },
        []
      )
    )

    forEach(labels, (label: string) => {
      const values: TableValue[] = []
      let labelObject: LabelItem | undefined

      // Step 5: detect optional "(subtitle)" and "[tooltip]" syntax in the label
      const tooltip = label.match(/\[.*?\]+/g)
      const [title, labelRemainder] = label.split(/[([]+/)
      const subtitle = label.match(/\(.*?\)+/g)

      if (labelRemainder) {
        // Label contains a subtitle or tooltip — convert it to a structured object
        labelObject = {
          title,
          subtitle: (subtitle && subtitle[0].replace(/[()']+/g, "")) as string,
          tooltip: (tooltip && createTooltipContent(tooltip)) ?? undefined,
        }
      }

      // Step 4: one cell per product; null when the product lacks this label
      for (let i = 0; i < list.length; i += 1) {
        values.push({
          value: get(list, [i, group, label], null),
          uuid: `${i}${group}${label}`,
        })
      }

      groupItems.push({ label: labelObject || label, values })
    })

    items.push({ name: group, items: groupItems })
  })

  return items
}

/**
 * Processes a list of products and returns grouped parameters ready for display
 * in the comparison table (ComparisonTable from UDS).
 *
 * Steps:
 *  1. Return null when the product list is empty.
 *  2. Filter out products without parameters defined in flatData.
 *  3. Merge the remaining products' parameters via compareProductParameters.
 *
 * Note: the "_" group name (Squidex placeholder for "untitled") is translated
 * to a readable label by `getTableSections` / `parseTableGroupName`.
 *
 * @param products - List of products to compare
 *
 * @returns Grouped parameters, or null when the product list is empty
 */
export function getProductParameters(
  products: ComparableProduct[]
): TableGroup[] | null {
  // Step 1: no products → nothing to display
  if (!products.length) {
    return null
  }

  // Step 2: filter out products without parameters and merge the data
  return compareProductParameters(
    products.filter((product: ComparableProduct): boolean =>
      has(product, "flatData.parameters")
    )
  )
}
